import clsx from 'clsx'
import React, { useMemo, useState } from 'react'

import { getInitials } from '#/utils/formatting'

import type { IProps } from './interfaces'
import styles from './styles.module.scss'

const PLACEHOLDER_MIN = 100
const PLACEHOLDER_MAX = 200
const BRAND_COLOR_CLASSES = [
  styles.brand0,
  styles.brand1,
  styles.brand2,
  styles.brand3,
  styles.brand4,
  styles.brand5,
] as const
const BRAND_COLOR_COUNT = BRAND_COLOR_CLASSES.length

function getPlaceholderUrl(): string {
  const id = Math.floor(Math.random() * (PLACEHOLDER_MAX - PLACEHOLDER_MIN + 1)) + PLACEHOLDER_MIN

  return `https://mockmind-api.uifaces.co/content/human/${id}.jpg`
}

/**
 * Simple hash that maps a string to a consistent index (0..count-1).
 * Uses char code sum — fast, deterministic, good distribution for short strings.
 */
function hashToIndex(text: string, count: number): number {
  let hash = 0

  for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) | 0

  return Math.abs(hash) % count
}

export const Avatar = ({
  imageUrl,
  name,
  size = 'medium',
  shape = 'circle',
  fallback = 'initials',
}: IProps): React.JSX.Element => {
  const [hasImageError, setHasImageError] = useState<boolean>(false)

  const hasExplicitImage = Boolean(imageUrl?.trim())
  const usePlaceholder = !hasExplicitImage && fallback === 'placeholder'

  const resolvedUrl = useMemo((): string | null => {
    if (hasExplicitImage) return imageUrl ?? null
    if (usePlaceholder) return getPlaceholderUrl()

    return null
  }, [imageUrl, hasExplicitImage, usePlaceholder])

  const initials = useMemo((): string => getInitials(name), [name])

  const colorIndex = useMemo((): number => hashToIndex(name, BRAND_COLOR_COUNT), [name])

  const handleError = (): void => {
    setHasImageError(true)
  }

  const showImage = resolvedUrl !== null && !hasImageError

  // Each rotation colour holds white initials at AA (tokens.scss)
  const colorClass = showImage ? undefined : BRAND_COLOR_CLASSES[colorIndex]

  return (
    <div
      className={clsx(styles.avatar, styles[size], styles[shape], colorClass)}
      data-testid="avatar"
      data-shape={shape}
      data-color={showImage ? undefined : colorIndex}
      aria-label={name}
    >
      {showImage ? (
        <img src={resolvedUrl} alt={name} className={styles.image} onError={handleError} />
      ) : (
        <span className={styles.initials} data-testid="avatar-initials">
          {initials}
        </span>
      )}
    </div>
  )
}
